import type { EntitlementValue, ScopeRef } from "@enduring-systems/entitlements";

/** The scopes an operator can target from the console (personal is never granted). */
export const EDITABLE_SCOPE_KINDS = ["platform", "enterprise", "facility", "user"] as const;

export type EditableScopeKind = (typeof EDITABLE_SCOPE_KINDS)[number];

export function scopeKindLabel(kind: EditableScopeKind): string {
  switch (kind) {
    case "platform":
      return "Platform";
    case "enterprise":
      // Product vocabulary: a business Space (the tenant) is an enterprise
      // (`spaces.id = enterprises.id`); the ScopeRef.kind stays "enterprise".
      return "Workspace";
    case "facility":
      // A "facility" scope is an org unit within the workspace; the
      // ScopeRef.kind stays "facility".
      return "Org unit";
    case "user":
      return "User";
  }
}

/**
 * Build a `ScopeRef` from the selected kind + id. Platform takes no id; the other
 * kinds require a non-blank id, returning `null` until one is chosen so the editor
 * can keep Save disabled.
 */
export function buildScope(kind: EditableScopeKind, id: string): ScopeRef | null {
  if (kind === "platform") {
    return { kind: "platform" };
  }
  const trimmed = id.trim();
  if (!trimmed) {
    return null;
  }
  return { kind, id: trimmed };
}

/**
 * The raw candidate value the admin-api expects, unwrapped from the control's
 * tagged `EntitlementValue`. The admin-api re-validates + re-tags it.
 */
export function rawValue(value: EntitlementValue): unknown {
  return value.value;
}
